import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  X,
  ShieldCheck,
  Users,
  CalendarDays,
  GraduationCap,
  ClipboardList,
  FileCheck2,
  FileText,
  ExternalLink,
  ListChecks,
  CheckCircle2,
  Download,
  Printer,
  Clock,
  MapPinned,
  UserCheck,
} from 'lucide-react';

interface ComiteSanteSecuriteRevisionModalProps {
  isOpen: boolean;
  onClose: () => void;
  contextLabel?: string;
}

function SectionCard({ title, icon: Icon, children }: { title: string; icon: React.ElementType; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm print:break-inside-avoid">
      <h3 className="mb-4 flex items-center gap-2 text-base font-bold text-slate-950">
        <Icon className="h-5 w-5 text-sky-700" />
        {title}
      </h3>
      {children}
    </section>
  );
}

const committeeMembers = [
  { name: 'Louis-Alexandre Hébert', role: 'Membre', poste: 'Direction / employeur' },
  { name: 'Mélissa Pelletier', role: 'Membre', poste: 'Administration & RH' },
  { name: 'Philippe Bolduc', role: 'Membre', poste: 'Cybersécurité' },
  { name: 'Joël Blais', role: 'Membre', poste: 'Technicien' },
  { name: 'Guillaume Cloutier', role: 'Membre', poste: 'Programmation' },
  { name: 'Benoît Dorval', role: 'Membre', poste: 'Télécom' },
];

const agendaItems = [
  'Mot de bienvenue et présentation des membres',
  'Présentation du mandat du comité',
  'Explication des fonctions principales du comité SST',
  'Rôles et responsabilités des membres',
  'Formation obligatoire des membres du comité, durée prévue de 7 heures',
  'Nomination du représentant en santé et sécurité et planification de sa formation distincte de 7 heures',
  'Orientation et objectifs à atteindre pour 2026-2027',
  'Discussion exploratoire sur les principaux risques et le plan de prévention',
  'Discussion des cas actuels',
  'Varia et prochaines étapes',
];

const meetingDocuments = [
  {
    title: 'Procès-verbal v4',
    detail: 'PV de travail à présenter pour la rencontre du 9 septembre 2026.',
    href: '/documents/comite-sst/proces-verbal-comite-sst-bz-2026-09-09-v4.pdf',
    type: 'PDF',
  },
  {
    title: 'Ordre du jour recommandé',
    detail: 'Version structurée pour la rencontre du 9 septembre 2026.',
    href: '/documents/comite-sst/ordre-du-jour-comite-sst-bz-recommande-2026-09-09.pdf',
    type: 'PDF',
  },
  {
    title: 'Ordre du jour source',
    detail: 'Document original transmis pour préparer la rencontre.',
    href: '/documents/comite-sst/ordre-du-jour-comite-sst-bz-source-2026-09-09.pdf',
    type: 'PDF',
  },
  {
    title: 'Ordre du jour source modifiable',
    detail: 'Version Word originale.',
    href: '/documents/comite-sst/ordre-du-jour-comite-sst-bz-source-2026-09-09.doc',
    type: 'DOC',
  },
  {
    title: 'Plan de prévention SST 2026-2027 v12 (2)',
    detail: 'Plan de prévention adopté comme base évolutive, incluant les FAS 2026-2027.',
    href: '/documents/comite-sst/plan-prevention-sst-bz-2026-2027-v12-2.pdf',
    type: 'PDF',
  },
];

const pvDecisions = [
  'Objectifs 2026-2027 adoptés : informer les employés, proposer des mécanismes de sensibilisation et contribuer à l’élimination des dangers.',
  'Plan de prévention SST 2026-2027 adopté à l’unanimité comme base de travail évolutive.',
  'FAS initiales retenues : premiers secours et trousses, DEA, sécurité incendie et évacuation.',
  'Formation obligatoire de membre du comité SST à compléter avant la prochaine rencontre lorsque possible.',
  'Canal de communication SST à étudier pour les questions, signalements ou enjeux, avec option de confidentialité.',
  'Composition du comité à bonifier : 1 à 2 membres à recruter, dont au moins une personne des ventes et au moins un autre employé.',
];

const pvActions = [
  {
    action: 'Premiers secours, RCR, affichage, trousses et registres',
    owner: 'Louis-Alexandre Hébert et Guillaume Cloutier',
    expected: 'État de situation, personnes formées, besoins de commande, affichage et ajustements au plan de prévention.',
  },
  {
    action: 'DEA',
    owner: 'Joël Blais',
    expected: 'Valider l’état, le fonctionnement, l’affichage, la formation requise et les actions à mettre en place.',
  },
  {
    action: 'Sécurité incendie et évacuation',
    owner: 'Philippe Bolduc',
    expected: 'Contacter la Ville de Québec / les pompiers pour clarifier exigences, conformité, exercice et prochaines étapes.',
  },
  {
    action: 'Formations obligatoires des membres',
    owner: 'Tous les membres actifs',
    expected: 'Compléter la formation de membre du comité SST avant la prochaine rencontre, lorsque possible.',
  },
  {
    action: 'Bonification de la composition du comité',
    owner: 'Louis-Alexandre Hébert',
    expected: 'Identifier 1 à 2 membres additionnels potentiels et faire un suivi au prochain comité.',
  },
];

const sourceDocuments = [
  {
    title: 'Affiche du comité SST BZ',
    detail: 'Présentation des membres, mandat et fonctions principales.',
    href: '/documents/comite-sst/affiche-comite-sst-bz-2026-07-22.pdf',
    type: 'PDF',
  },
];

const auditDocuments = [
  {
    title: 'Audit général SST',
    detail: 'Audit numérisé du 9 septembre 2026, reçu après la première rencontre du comité SST.',
    href: '/documents/comite-sst/audit-general-sst-bz-2026-09-09.pdf',
    type: 'PDF',
    date: '2026-09-09',
    author: 'Comité SST',
    status: 'À analyser',
  },
  {
    title: 'Audit général SST',
    detail: 'Audit numérisé du 29 juillet 2026, conservé comme preuve et référence initiale.',
    href: '/documents/comite-sst/audit-general-sst-bz-2026-07-29.pdf',
    type: 'PDF',
    date: '2026-07-29',
    author: 'Comité SST',
    status: 'À classer',
  },
];

const mandateItems = [
  'Favoriser la participation employeur-travailleurs en santé et sécurité.',
  'Identifier, analyser et prioriser les risques SST de l’établissement.',
  'Recommander des mesures de prévention concrètes et en suivre l’avancement.',
  'Soutenir le programme de prévention BZ et la conservation des preuves.',
  'Documenter les rencontres, décisions, actions, formations et suivis.',
];

const annualObjectives = [
  'Faire approuver le mandat du comité santé et sécurité.',
  'Tenir les rencontres prévues et conserver les procès-verbaux.',
  'Faire approuver et suivre le plan de prévention 2026-2027.',
  'Classer les FAS 2026-2027 à l’extérieur des rencontres individuelles.',
  'Documenter les actions, décisions et preuves liées à la Loi 27.',
];

const preventionPlanItems = [
  'Plan de prévention SST 2026-2027 v12 (2) déposé dans la carte du comité.',
  'Statut : adopté à l’unanimité comme base de travail évolutive lors de la rencontre du 9 septembre 2026.',
  'Historique des versions et preuves d’approbation à conserver ici avec les procès-verbaux.',
];

const fasItems = [
  'FAS 2026-2027 intégrées au plan de prévention SST 2026-2027 v12 (2).',
  'Les FAS sont des objets annuels : elles restent dans la carte du comité, pas dans une rencontre ponctuelle.',
  'Chaque FAS devra pouvoir être reliée au plan de prévention et aux suivis du comité.',
];

const proofZones = [
  'Ordres du jour',
  'Procès-verbaux',
  'Présences',
  'Recommandations',
  'Décisions',
  'Suivis d’actions',
  'Preuves de formation',
  'Programme de prévention',
  'Fiches d’action spécifiques',
  'Registres accidents / incidents',
  'Inspections',
  'Analyses de risques',
];

function DocumentLink({ document }: { document: { title: string; detail: string; href: string; type: string } }) {
  return (
    <a
      href={document.href}
      download
      target="_blank"
      rel="noreferrer"
      className="group flex h-full items-start justify-between gap-4 rounded-lg border border-slate-200 bg-slate-50 p-4 text-left transition-colors hover:border-sky-300 hover:bg-sky-50"
    >
      <div>
        <div className="mb-2 inline-flex items-center gap-2 rounded border border-slate-200 bg-white px-2 py-1 text-[11px] font-bold uppercase text-slate-600">
          <FileText className="h-3.5 w-3.5" />
          {document.type}
        </div>
        <div className="font-semibold text-slate-950">{document.title}</div>
        <p className="mt-1 text-sm leading-relaxed text-slate-600">{document.detail}</p>
      </div>
      <Download className="mt-1 h-5 w-5 shrink-0 text-sky-700 transition-transform group-hover:translate-y-0.5" />
    </a>
  );
}

export default function ComiteSanteSecuriteRevisionModal({
  isOpen,
  onClose,
  contextLabel = 'Contenu en révision',
}: ComiteSanteSecuriteRevisionModalProps) {
  const [activeTab, setActiveTab] = useState<'comite' | 'audits'>('comite');

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/75 p-4 backdrop-blur-sm print:static print:block print:bg-white print:p-0"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.98, y: 8 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => e.stopPropagation()}
          className="max-h-[92vh] w-full max-w-7xl overflow-y-auto rounded-xl bg-slate-100 shadow-2xl print:max-h-none print:overflow-visible print:rounded-none print:shadow-none"
        >
          <div className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 px-6 py-4 backdrop-blur print:static">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="mb-1 text-xs font-bold uppercase tracking-[0.2em] text-sky-700">{contextLabel}</p>
                <h2 className="text-2xl font-black text-slate-950">Comité santé et sécurité</h2>
                <p className="mt-1 max-w-4xl text-sm leading-relaxed text-slate-600">
                  Espace PRIsME autonome pour suivre le comité SST : membres, mandat, objectifs annuels,
                  rencontres, procès-verbaux, plan de prévention 2026-2027, FAS et preuves conservées.
                </p>
              </div>
              <div className="flex items-center gap-2 print:hidden">
                <button
                  onClick={() => window.print()}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  <Printer className="h-4 w-4" />
                  PDF
                </button>
                <button onClick={onClose} className="rounded-lg p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900">
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
          </div>

          <div className="space-y-6 p-6 print:p-0">
            <div className="grid gap-4 md:grid-cols-4">
              <div className="rounded-lg border border-sky-200 bg-sky-900 p-5 text-white shadow-sm md:col-span-2">
                <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-sky-100">
                  <CalendarDays className="h-5 w-5" />
                  Rencontre tenue
                </div>
                <div className="text-3xl font-black">9 septembre 2026</div>
                <p className="mt-2 text-sm leading-relaxed text-sky-50">
                  Première rencontre du comité SST BZ. Les travaux ont été lancés, le mandat a été clarifié
                  et les priorités de prévention 2026-2027 ont été convenues.
                </p>
              </div>

              <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-600">
                  <Users className="h-5 w-5 text-sky-700" />
                  Membres
                </div>
                <div className="text-3xl font-black text-slate-950">{committeeMembers.length}</div>
                <p className="mt-2 text-sm text-slate-600">Liste initiale reçue, à corriger si quelqu’un manque.</p>
              </div>

              <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-600">
                  <FileCheck2 className="h-5 w-5 text-sky-700" />
                  Documents
                </div>
                <div className="text-3xl font-black text-slate-950">{meetingDocuments.length + sourceDocuments.length + auditDocuments.length}</div>
                <p className="mt-2 text-sm text-slate-600">Tous les fichiers peuvent être ouverts ou téléchargés.</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 rounded-lg border border-slate-200 bg-white p-2 shadow-sm print:hidden">
              <button
                type="button"
                onClick={() => setActiveTab('comite')}
                className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-bold transition-colors ${
                  activeTab === 'comite'
                    ? 'bg-sky-900 text-white'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-950'
                }`}
              >
                <Users className="h-4 w-4" />
                Comité
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('audits')}
                className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-bold transition-colors ${
                  activeTab === 'audits'
                    ? 'bg-emerald-800 text-white'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-950'
                }`}
              >
                <ClipboardList className="h-4 w-4" />
                Audits
              </button>
            </div>

            {activeTab === 'audits' ? (
              <div className="space-y-6">
                <SectionCard title="Audits de sécurité" icon={ClipboardList}>
                  <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm leading-relaxed text-emerald-950">
                    Les audits restent dans la carte du comité SST pour garder la preuve, le suivi et les membres au même endroit.
                    Les actions correctives pourront ensuite être créées séparément au besoin.
                  </div>
                  <div className="grid gap-3 md:grid-cols-2">
                    {auditDocuments.map((document) => (
                      <div key={document.href} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                        <div className="mb-3 flex flex-wrap items-center gap-2">
                          <span className="rounded border border-slate-200 bg-white px-2 py-1 text-[11px] font-bold uppercase text-slate-600">
                            {document.type}
                          </span>
                          <span className="rounded border border-emerald-200 bg-emerald-100 px-2 py-1 text-[11px] font-bold uppercase text-emerald-800">
                            {document.status}
                          </span>
                        </div>
                        <h4 className="text-lg font-black text-slate-950">{document.title}</h4>
                        <p className="mt-1 text-sm leading-relaxed text-slate-600">{document.detail}</p>
                        <div className="mt-4 grid gap-2 text-sm text-slate-700 sm:grid-cols-2">
                          <div>
                            <span className="block text-xs font-bold uppercase text-slate-500">Date</span>
                            {document.date}
                          </div>
                          <div>
                            <span className="block text-xs font-bold uppercase text-slate-500">Produit par</span>
                            {document.author}
                          </div>
                        </div>
                        <a
                          href={document.href}
                          download
                          target="_blank"
                          rel="noreferrer"
                          className="mt-4 inline-flex items-center gap-2 rounded-lg bg-sky-900 px-3 py-2 text-sm font-bold text-white hover:bg-sky-800"
                        >
                          <Download className="h-4 w-4" />
                          Ouvrir le PDF
                        </a>
                      </div>
                    ))}
                  </div>
                </SectionCard>

                <SectionCard title="Membres liés aux audits" icon={Users}>
                  <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {committeeMembers.map((member) => (
                      <div key={member.name} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                        <div className="font-semibold text-slate-950">{member.name}</div>
                        <div className="text-xs text-slate-500">{member.poste}</div>
                      </div>
                    ))}
                  </div>
                </SectionCard>
              </div>
            ) : (
              <>
            <div className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
              <SectionCard title="Rencontre du 9 septembre 2026" icon={CalendarDays}>
                <div className="mb-5 grid gap-3 sm:grid-cols-3">
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <div className="mb-1 flex items-center gap-2 text-xs font-bold uppercase text-slate-500">
                      <Clock className="h-4 w-4" />
                      Statut
                    </div>
                    <p className="font-semibold text-slate-950">PV v4 à présenter</p>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <div className="mb-1 flex items-center gap-2 text-xs font-bold uppercase text-slate-500">
                      <MapPinned className="h-4 w-4" />
                      Objet
                    </div>
                    <p className="font-semibold text-slate-950">Lancement du comité SST</p>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <div className="mb-1 flex items-center gap-2 text-xs font-bold uppercase text-slate-500">
                      <UserCheck className="h-4 w-4" />
                      Participants
                    </div>
                    <p className="font-semibold text-slate-950">5 présents, Benoît excusé</p>
                  </div>
                </div>

                <h4 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-500">Ordre du jour</h4>
                <ol className="space-y-2">
                  {agendaItems.map((item, index) => (
                    <li key={item} className="flex gap-3 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-sky-700 text-xs font-bold text-white">
                        {index + 1}
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ol>
              </SectionCard>

              <SectionCard title="Membres du comité" icon={Users}>
                <div className="space-y-2">
                  {committeeMembers.map((member) => (
                    <div key={member.name} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                      <div className="font-semibold text-slate-950">{member.name}</div>
                      <div className="text-sm text-slate-600">{member.role}</div>
                      <div className="text-xs text-slate-500">Poste : {member.poste}</div>
                    </div>
                  ))}
                </div>
              </SectionCard>
            </div>

            <SectionCard title="Mandat du comité" icon={ShieldCheck}>
              <div className="grid gap-3 md:grid-cols-2">
                {mandateItems.map((item) => (
                  <div key={item} className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-sky-700" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </SectionCard>

            <SectionCard title="Objectifs annuels" icon={CheckCircle2}>
              <div className="grid gap-3 md:grid-cols-2">
                {annualObjectives.map((item) => (
                  <div key={item} className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </SectionCard>

            <SectionCard title="Rencontres et procès-verbaux" icon={FileText}>
              <div className="grid gap-3 md:grid-cols-3">
                {meetingDocuments.map((document) => (
                  <DocumentLink key={document.href} document={document} />
                ))}
              </div>
              <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm leading-relaxed text-emerald-950">
                Le PV v4 du 9 septembre 2026 est conservé comme version à présenter. Il confirme le lancement du comité,
                l’adoption du plan de prévention comme base évolutive, les premières FAS et les suivis avant le 2 décembre 2026.
              </div>
            </SectionCard>

            <SectionCard title="Synthèse du procès-verbal v4" icon={FileCheck2}>
              <div className="grid gap-3 lg:grid-cols-2">
                <div className="space-y-2">
                  <h4 className="text-sm font-bold uppercase tracking-wide text-slate-500">Décisions et orientations</h4>
                  {pvDecisions.map((decision) => (
                    <div key={decision} className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" />
                      <span>{decision}</span>
                    </div>
                  ))}
                </div>
                <div className="space-y-2">
                  <h4 className="text-sm font-bold uppercase tracking-wide text-slate-500">Actions avant le 2 décembre 2026</h4>
                  {pvActions.map((item) => (
                    <div key={item.action} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                      <div className="font-semibold text-slate-950">{item.action}</div>
                      <div className="mt-1 text-xs font-bold uppercase text-sky-700">{item.owner}</div>
                      <p className="mt-1 text-sm leading-relaxed text-slate-600">{item.expected}</p>
                    </div>
                  ))}
                </div>
              </div>
            </SectionCard>

            <div className="grid gap-6 lg:grid-cols-2">
              <SectionCard title="Plan de prévention 2026-2027" icon={ClipboardList}>
                <div className="space-y-2">
                  {preventionPlanItems.map((item) => (
                    <div key={item} className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                      {item}
                    </div>
                  ))}
                </div>
              </SectionCard>

              <SectionCard title="FAS 2026-2027" icon={ListChecks}>
                <div className="space-y-2">
                  {fasItems.map((item) => (
                    <div key={item} className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                      {item}
                    </div>
                  ))}
                </div>
              </SectionCard>
            </div>

            <SectionCard title="Documents sources du comité" icon={ClipboardList}>
              <div className="grid gap-3 md:grid-cols-2">
                {sourceDocuments.map((document) => (
                  <DocumentLink key={document.href} document={document} />
                ))}
              </div>
            </SectionCard>

            <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
              <SectionCard title="Formation et preuves" icon={GraduationCap}>
                <div className="overflow-hidden rounded-lg border border-slate-200">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100 text-left text-xs font-bold uppercase text-slate-500">
                      <tr>
                        <th className="px-4 py-3">Élément</th>
                        <th className="px-4 py-3">Statut</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 bg-white">
                      <tr>
                        <td className="px-4 py-3 text-slate-700">
                          Demande d’inscription envoyée aux membres actifs - formation membre comité SST à compléter avant le 2 décembre 2026
                        </td>
                        <td className="px-4 py-3 font-semibold text-emerald-700">Envoyée le 2026-09-10</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 text-slate-700">Autres membres - formation obligatoire 7 h</td>
                        <td className="px-4 py-3 font-semibold text-sky-700">À déposer</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 text-slate-700">Représentant SST - formation distincte 7 h</td>
                        <td className="px-4 py-3 font-semibold text-sky-700">À confirmer</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </SectionCard>

              <SectionCard title="Registre de preuves à conserver" icon={ListChecks}>
                <div className="grid gap-2 sm:grid-cols-2">
                  {proofZones.map((zone) => (
                    <div key={zone} className="flex items-center gap-2 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-sky-700" />
                      {zone}
                    </div>
                  ))}
                </div>
              </SectionCard>
            </div>

            <SectionCard title="Références CNESST à garder avec le dossier" icon={ExternalLink}>
              <div className="grid gap-3 md:grid-cols-2">
                <a className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm font-semibold text-sky-800 hover:bg-sky-50" href="https://www.cnesst.gouv.qc.ca/fr/prevention-securite/organiser-prevention/mesures-specifiques-etablissements/appliquer-mecanismes-prevention-participation/mecanismes-participation/mettre-en-place-comite-sante-securite/comite-sante-securite" target="_blank" rel="noreferrer">
                  Comité de santé et sécurité - CNESST
                </a>
                <a className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm font-semibold text-sky-800 hover:bg-sky-50" href="https://www.cnesst.gouv.qc.ca/fr/prevention-securite/organiser-prevention/mesures-specifiques-etablissements/appliquer-mecanismes-prevention-participation/mecanismes-participation/mettre-en-place-comite-sante-securite/fonctions-regles-fonctionnement-comite-sante" target="_blank" rel="noreferrer">
                  Fonctions et règles de fonctionnement - CNESST
                </a>
              </div>
            </SectionCard>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
